import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Card, Dialog, Input, Textarea, Alert } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, StatusBadge, QueryState, StatCard, Money } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { formatINR, formatDate } from '@/lib/format';
import DataTable from '../components/DataTable';
import { usePlatformCan } from '../lib/access';

const SETTLED = { payout: 'Deducted from payout', recorded: 'Payment recorded', waived: 'Waived' };

/**
 * ADMIN-05 Plans & billing. Plans are versioned (a change is v<n+1>; stores pay the
 * new price from their next invoice). Every store is billed on the 1st (IST); the
 * invoice is netted from its next weekly payout (D20); overdue invoices raise an
 * exception-desk item (D21).
 */
export default function Plans() {
  const nav = useNavigate();
  const canManage = usePlatformCan('plans.manage');
  const plans = useQuery('platformPlans', undefined, { select: (r) => r.data });
  const merchants = useQuery('platformMerchants', undefined, { select: (r) => r.data });
  const subs = useQuery('platformSubscriptions', undefined, { select: (r) => r.data });
  const invoices = useQuery('platformInvoices', undefined, { select: (r) => r.data });
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');

  return (
    <div className="space-y-6">
      <PageHeader title="Plans & billing" subtitle="ADMIN-05 · subscription plans, merchant subscriptions and platform invoices" />
      {msg ? <Alert variant="success" title={msg} dismissible onDismiss={() => setMsg('')} /> : null}

      <QueryState query={plans}>
        {(list) => (
          <div className="grid gap-4 md:grid-cols-3">
            {list.map((p) => {
              const count = (merchants.data || []).filter((m) => m.plan_key === p.key).length;
              return (
                <Card key={p.key} className="p-4 space-y-3 flex flex-col">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-lg">{p.name}</span>
                    <Badge variant="success" size="sm">v{p.version}</Badge>
                  </div>
                  <div className="text-2xl font-bold">{formatINR(p.price_paise)}<span className="text-sm font-normal opacity-60"> /{p.billing_period}</span></div>
                  <ul className="text-sm space-y-1 opacity-80 flex-1">{(p.features || []).map((f) => <li key={f}>✓ {f}</li>)}</ul>
                  <div className="text-sm opacity-70">{p.max_units == null ? 'Unlimited units' : `Up to ${p.max_units} units`} · {count} merchant{count === 1 ? '' : 's'}</div>
                  {p.updated_by ? <div className="text-xs opacity-60">Updated {formatDate(p.updated_at)} by {p.updated_by}</div> : null}
                  {canManage ? <Button variant="outline" fullWidth onClick={() => setEditing(p)}>Manage</Button> : null}
                </Card>
              );
            })}
          </div>
        )}
      </QueryState>

      {subs.data && invoices.data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Active subscriptions" value={subs.data.filter((s) => s.status === 'active').length} hint={`${subs.data.length} stores`} />
          <StatCard label="Billed monthly" value={formatINR(subs.data.filter((s) => s.status !== 'paused').reduce((a, s) => a + (s.amount_paise || 0), 0))} hint="before GST" />
          <StatCard label="Open invoices" value={invoices.data.filter((i) => ['open', 'overdue'].includes(i.status)).length} hint="netted from the next payout" />
          <StatCard label="Past due" value={subs.data.filter((s) => s.status === 'past_due').length} hint="on the exception desk" />
        </div>
      ) : null}

      <SectionCard title="Merchant subscriptions">
        <QueryState query={subs} skeleton="table">
          {(rows) => (
            <DataTable
              data={rows || []}
              onRowClick={(r) => nav(`/merchants/${r.merchant_id}?tab=subscription`)}
              columns={[
                { key: 'merchant', label: 'Merchant', render: (r) => <span className="font-medium">{r.merchant}</span> },
                { key: 'plan_key', label: 'Plan', render: (r) => <Badge variant="primary">{r.plan_key}</Badge> },
                { key: 'amount_paise', label: 'Amount', render: (r) => <Money paise={r.amount_paise} /> },
                { key: 'next_billing_at', label: 'Next invoice', hideOnMobile: true, render: (r) => formatDate(r.next_billing_at) },
                { key: 'payment_method', label: 'Paid by', hideOnMobile: true },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              ]}
            />
          )}
        </QueryState>
      </SectionCard>

      <SectionCard title="Invoices">
        <QueryState query={invoices} skeleton="table">
          {(rows) => (
            <DataTable
              data={rows || []}
              pagination={(rows || []).length > 10}
              pageSize={10}
              columns={[
                { key: 'id', label: 'Invoice', render: (r) => <span className="font-medium">{r.id}</span> },
                { key: 'merchant', label: 'Merchant' },
                { key: 'period', label: 'Period', hideOnMobile: true },
                { key: 'total_paise', label: 'Total (incl. GST)', render: (r) => <Money paise={r.total_paise} /> },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                { key: 'settled_via', label: 'Settled', hideOnMobile: true, wrap: true, render: (r) => (r.settled_via ? `${SETTLED[r.settled_via] || r.settled_via}${r.settlement_ref ? ` · ${r.settlement_ref}` : ''}` : `Due ${formatDate(r.due_at)}`) },
              ]}
            />
          )}
        </QueryState>
      </SectionCard>

      {editing ? (
        <PlanEditor
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={(p) => {
            plans.setData((all) => all.map((x) => (x.key === p.key ? p : x)));
            setMsg(`${p.name} is now v${p.version}. Stores pay the new price from their next invoice.`);
          }}
        />
      ) : null}
    </div>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const save = useMutation('platformPlanUpdate');
  const history = useQuery('platformPlanHistory', { key: plan.key }, { select: (r) => r.data });
  const [form, setForm] = useState({
    name: plan.name,
    price: String(plan.price_paise / 100),
    max_units: plan.max_units == null ? '' : String(plan.max_units),
    features: (plan.features || []).join('\n'),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const rupees = Number(form.price);
  const priceError = form.price === '' || !Number.isFinite(rupees) || rupees < 0 ? 'Enter a price in rupees, e.g. 4999' : '';
  const unitsError = form.max_units !== '' && !/^\d+$/.test(form.max_units) ? 'Whole number, or leave empty for unlimited' : '';

  const submit = async () => {
    try {
      const r = await save.run({
        key: plan.key,
        name: form.name.trim() || plan.name,
        price_paise: Math.round(rupees * 100),
        max_units: form.max_units === '' ? null : Number(form.max_units),
        features: form.features.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      onSaved(r.data);
      onClose();
    } catch {
      /* save.error is shown below */
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={`Manage ${plan.name} plan`}
      size="md"
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={save.loading} disabled={!!priceError || !!unitsError}>Save as v{plan.version + 1}</Button>
        </div>
      )}
    >
      <div className="space-y-3">
        <Alert variant="info" title="Existing stores keep their price until their next invoice">Saving creates version v{plan.version + 1}. Invoices are issued on the 1st of each month.</Alert>
        {save.error ? <Alert variant="error" title="Not saved">{save.error}</Alert> : null}
        <Input label="Name" value={form.name} onChange={set('name')} />
        <Input label="Price per month (₹)" type="number" value={form.price} onChange={set('price')} error={priceError} />
        <Input label="Unit limit" type="number" value={form.max_units} onChange={set('max_units')} error={unitsError} helperText="Empty = unlimited" />
        <Textarea label="Features (one per line)" value={form.features} onChange={set('features')} rows={4} />
        <QueryState query={history}>
          {(rows) => (
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">History</h3>
              <ul className="text-xs opacity-80 space-y-1">
                {rows.map((v) => <li key={v.version}>v{v.version} · {formatINR(v.price_paise)} · {v.max_units == null ? 'unlimited' : `${v.max_units} units`} · {v.author} · {formatDate(v.at)}</li>)}
              </ul>
            </div>
          )}
        </QueryState>
      </div>
    </Dialog>
  );
}
